import React, { useState, useEffect, useContext, createContext, useMemo } from 'react'
import { useAuth, userRoles } from './useAuth.js'
import api from '../services/api';
import { isActiveProject } from '../utils/helpers';

const ProjectsContext = createContext();

export function ProjectsProvider({ children }) {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [allProjects, setAllProjects] = useState([]);

  // Fetch Projects from API
  const fetchProjects = async () => {
    if (!user) return;
    setLoading(true);
    try {
      // Tried three times. One failed request right after sign-in used to leave an empty dashboard,
      // with no message, until the person signed in again.
      let response;
      for (let attempt = 0; ; attempt++) {
        try { response = await api.get('projects/'); break; }
        catch (e) {
          if (attempt >= 2 || e?.response?.status === 401 || e?.response?.status === 403) throw e;
          await new Promise(r => setTimeout(r, 800 * (attempt + 1)));
        }
      }
      setAllProjects(response.data);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch projects:", err);
      setError("Failed to load projects.");
    } finally {
      setLoading(false);
    }
  };

  // Initial Fetch
  useEffect(() => {
    if (user) {
      fetchProjects();
    } else {
      setAllProjects([]);
      setLoading(false);
    }
  }, [user]);

  // Create Project (then create its milestones if any were added in the wizard)
  const createProject = async (projectData) => {
    try {
      const payload = {
        ...projectData,
        total_budget: parseFloat(projectData.totalBudget || projectData.estimatedBudget || 0),
        start_date: projectData.startDate,
        end_date: projectData.endDate,
        // Projects go live immediately on creation (no admin-approval gate) — the team is
        // notified and can start straight away.
        status: "In Progress"
      };
      // Admin may have chosen a specific PM to own the project
      if (projectData.projectManagerId) {
        payload.project_manager_id = parseInt(projectData.projectManagerId);
      }
      // The PM's/client's own project reference (optional), preserved alongside ours
      payload.external_ref = (projectData.external_ref || '').trim() || null;
      // Budget model: Fixed (lump sum) | Actual (reimbursable) | Hybrid
      payload.budget_type = projectData.budgetType || projectData.budget_type || 'Fixed';
      // Persist the consultant role fields (previously form-only, lost after create)
      payload.job_title = (projectData.jobTitle || '').trim() || null;
      payload.purpose_of_position = (projectData.purposeOfPosition || '').trim() || null;
      // Invited team members (PM/Finance/Funder/Consultant)
      if (Array.isArray(projectData.teamMemberIds)) payload.team_member_ids = projectData.teamMemberIds;
      // v2 nested projects: create under a parent (sub-project)
      if (projectData.parentProjectId) payload.parent_project_id = parseInt(projectData.parentProjectId);
      // Project Files are uploaded separately (below) to the project's document store —
      // don't send the (non-serialisable) File objects in the JSON create payload.
      delete payload.files;

      const response = await api.post('/projects/', payload);
      const newProject = response.data;

      // Upload any attached Project Files into the project's shared documents so they
      // actually persist and show up in the Documents tab (previously they were lost).
      for (const pf of (projectData.files || [])) {
        if (!pf?.file) continue;
        try {
          const fd = new FormData();
          fd.append('title', pf.name || pf.file.name || 'Project file');
          fd.append('file', pf.file);
          await api.post(`/projects/${newProject.id}/documents/`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch {
          // non-fatal — project still created even if a file upload fails
        }
      }

      // Create each milestone that was added in the wizard
      const milestones = projectData.milestones || [];
      const assignMap = {};   // consultantId -> [created milestone id]  (per-milestone assignment)
      for (const m of milestones) {
        const title = (m.description || m.title || '').trim();
        if (!title) continue;
        try {
          // Hybrid projects mix Fixed and Actuals milestones. An Actuals milestone
          // carries no fixed figure — the consultant claims it (with bills) at invoice.
          const isHybridActual = (projectData.budgetType === 'Hybrid') && (m.mode === 'actual');
          const amt = isHybridActual ? 0 : (m.amount ? parseFloat(m.amount) : 0);
          const isReimbursable = projectData.budgetType === 'Actual' || isHybridActual;
          const tb = isReimbursable && m.tentativeBudget ? parseFloat(m.tentativeBudget) : null;
          const msResp = await api.post(`/projects/${newProject.id}/milestones/`, {
            title,
            project_id: newProject.id,
            payment_mode: isHybridActual ? 'Actual' : 'Fixed',
            payment_amount: amt,
            amount_released_on_completion: amt,
            due_date: m.deadline || null,
            tentative_budget: tb,
          });
          // Remember which consultant this milestone was assigned to on the Assign step.
          if (msResp?.data?.id && m.assignConsultantId) {
            (assignMap[m.assignConsultantId] = assignMap[m.assignConsultantId] || []).push(msResp.data.id);
          }
          // If user attached a brief file to this milestone in the wizard, upload it now
          if (m.briefFile && msResp?.data?.id) {
            try {
              const fd = new FormData();
              fd.append('file', m.briefFile);
              await api.post(`/projects/milestones/${msResp.data.id}/brief`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });
            } catch {
              // non-fatal — milestone created, brief upload just failed silently
            }
          }
        } catch {
          // non-fatal — project still created even if a milestone fails
        }
      }

      // OFFER milestones to consultants. If the PM assigned specific milestones to specific
      // consultants on the Assign step, offer each consultant EXACTLY their milestone subset
      // (multi-consultant, no clash). Otherwise fall back to offering everything to the single
      // chosen consultant (legacy single-consultant projects).
      let warning = null;
      const assignedIds = Object.keys(assignMap);
      if (assignedIds.length > 0) {
        for (const cid of assignedIds) {
          try {
            await api.put(`/projects/${newProject.id}/assign/`, null, {
              params: { consultant_id: parseInt(cid), milestone_ids: assignMap[cid] },
              // repeat the key (?milestone_ids=1&milestone_ids=2) for FastAPI List[int];
              // the default bracket form (milestone_ids[]=1) is ignored → would offer ALL.
              paramsSerializer: { indexes: null },
            });
          } catch (assignErr) {
            warning = assignErr.response?.data?.detail
              || 'The project was created, but some milestones could not be offered. Offer them from the project\'s Team tab once each consultant is active.';
          }
        }
      } else {
        const chosenConsultant = projectData.consultantId || projectData.consultant_id;
        if (chosenConsultant) {
          try {
            await api.put(`/projects/${newProject.id}/assign/`, null, {
              params: { consultant_id: parseInt(chosenConsultant) },
            });
          } catch (assignErr) {
            warning = assignErr.response?.data?.detail
              || 'The project was created, but milestones could not be offered to the consultant. Offer them from the project\'s Team tab once the consultant is active.';
          }
        }
      }

      setAllProjects(prev => [...prev, newProject]);
      return { success: true, project: newProject, warning }
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      return { success: false, error: err.message }
    }
  }

  // Update Project (calls PUT /projects/{id})
  const updateProject = async (projectId, updateData) => {
    try {
      const response = await api.put(`/projects/${projectId}/`, updateData);
      // Update local state with server response
      setAllProjects(prev =>
        prev.map(p => p.id === projectId ? response.data : p)
      );
      return { success: true, project: response.data }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  }

  // Delete Project (calls DELETE /projects/{id})
  const deleteProject = async (projectId) => {
    try {
      await api.delete(`/projects/${projectId}/`);
      setAllProjects(prev => prev.filter(p => p.id !== projectId));
      return { success: true }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message;
      return { success: false, error: errorMsg }
    }
  }

  // Approve Project (calls POST /projects/{id}/approve)
  const approveProject = async (projectId) => {
    try {
      const response = await api.post(`/projects/${projectId}/approve/`, {});
      setAllProjects(prev =>
        prev.map(p => p.id === projectId ? response.data : p)
      );
      return { success: true, project: response.data }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message;
      return { success: false, error: errorMsg }
    }
  }

  // Reject Project (calls POST /projects/{id}/reject)
  const rejectProject = async (projectId, reason = '') => {
    try {
      const response = await api.post(`/projects/${projectId}/reject/?reason=${encodeURIComponent(reason)}`, {});
      setAllProjects(prev =>
        prev.map(p => p.id === projectId ? response.data : p)
      );
      return { success: true, project: response.data }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message;
      return { success: false, error: errorMsg }
    }
  }

  // Hold Project (calls PUT /projects/{id} with status update)
  const holdProject = async (projectId) => {
    try {
      const response = await api.put(`/projects/${projectId}/`, { status: 'On Hold' });
      setAllProjects(prev =>
        prev.map(p => p.id === projectId ? response.data : p)
      );
      return { success: true }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message;
      return { success: false, error: errorMsg }
    }
  }

  // --- v3.2 nested projects / scoped roles ---
  const getSubprojects = async (projectId) => {
    try { const r = await api.get(`/projects/${projectId}/children`); return r.data || [] }
    catch { return [] }
  }
  const getProjectRollup = async (projectId) => {
    try { const r = await api.get(`/projects/${projectId}/rollup`); return r.data }
    catch { return null }
  }
  // Master PM/Admin promotes the consultant on a milestone to lead a sub-project under this one.
  const promoteMilestoneLead = async (projectId, milestoneId, consultantId) => {
    try {
      const r = await api.post(
        `/projects/${projectId}/milestones/${milestoneId}/promote-lead`,
        consultantId ? { consultant_id: consultantId } : {},
      )
      return { success: true, ...r.data }
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || err.message }
    }
  }

  // --- Filtering Logic (Client side for now to match UI expectations) ---
  const filteredProjects = useMemo(() => {
    if (!user) return [];

    // Simple mapper:
    const mapProject = (p) => ({
      ...p,
      totalBudget: p.total_budget,
      spentBudget: p.spent_budget,
      approvedBudget: p.approved_budget,
      // remaining = what's left of the APPROVED budget after spend (API's remaining_budget
      // isn't always present; compute a reliable fallback so it never reads $0 wrongly).
      remainingBudget: p.remaining_budget ?? Math.max(0, (p.approved_budget || 0) - (p.spent_budget || 0)),
      // ... mappings
      progress: p.progress || 0,
      status: p.status || 'Pending',
      projectManagerId: p.project_manager_id,
      funderId: p.funder_id,
      consultantId: p.consultant_id,
      parentProjectId: p.parent_project_id,   // v2 nested projects
      // What a card shows at a glance. createdAt is when it was entered into ProTrack, which is
      // not startDate — that is the plan, and is often months away or backdated.
      createdAt: p.created_at,
      milestonesTotal: p.milestones_total ?? 0,
      milestonesDone: p.milestones_done ?? 0,
      milestonesInFlight: p.milestones_in_flight ?? 0,
      stage: p.stage || null,
      milestoneSteps: p.milestone_steps || [],
      // Consultants live on the milestones, so the server gathers them; [] keeps every consumer
      // free of a null check.
      consultantNames: p.consultant_names || [],
      coManagerNames: p.co_manager_names || [],
      // What is coming next, by name. The counts say how much is left, not what it is.
      nextMilestone: p.next_milestone || null,
      nextMilestoneDue: p.next_milestone_due || null,
      nextMilestoneConsultant: p.next_milestone_consultant || null
    });

    const projectList = Array.isArray(allProjects) ? allProjects : [];
    // Defensive stable sort by id — the backend now orders by id too, but this protects the
    // list from re-arranging even if a future endpoint/cache path returns a different order
    // (was the root of "cards reshuffle after opening a project and coming back").
    const sorted = [...projectList].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
    const mapped = sorted.map(mapProject);

    if (user.role === userRoles.ADMIN || user.role === userRoles.FINANCE) {
      return mapped;
    }
    // EVERY OTHER ROLE: trust the server, do not re-filter.
    //
    // /projects/ has already applied _can_view_project to every row, and that check knows about
    // things this code cannot: team membership, delegated approver scope, sub-project
    // inheritance, offered-but-not-yet-accepted milestones, the manage_all_projects capability.
    // Re-filtering here on one field can only ever DISCARD something the server deliberately
    // sent.
    //
    // It also buys nothing in return. The rows are already in the browser by the time this runs,
    // so hiding them is presentation, not security — the guard that matters is the one on the
    // server, and it is already there.
    //
    // This was learned three times. A consultant could not see freshly-offered work, because
    // project.consultant_id stays null until a contract is approved. The Master PM could not see
    // projects she manages by capability rather than by ownership. And a PM added to a project's
    // team got the notification, could open the project from the link, and then could not find
    // it on this page — the server had sent it and this line dropped it.
    return mapped;
  }, [user, allProjects]);

  // Stats
  const getProjectStats = useMemo(() => {
    const list = Array.isArray(filteredProjects) ? filteredProjects : [];
    const totalBudget = list.reduce((sum, p) => sum + (p.approvedBudget || 0), 0)
    const totalSpent = list.reduce((sum, p) => sum + (p.spentBudget || 0), 0)
    const totalRemaining = totalBudget - totalSpent
    const avgProgress = list.length > 0 ? list.reduce((sum, p) => sum + (p.progress || 0), 0) / list.length : 0

    return {
      total: list.length,
      active: list.filter(isActiveProject).length,
      completed: list.filter(p => p.status === 'Completed').length,
      planning: list.filter(p => p.status === 'Pending Approval').length,
      totalBudget,
      totalSpent,
      totalRemaining,
      avgProgress: Math.round(avgProgress),
      utilizationRate: totalBudget > 0 ? Math.round((totalSpent / totalBudget) * 100) : 0
    }
  }, [filteredProjects]);

  const getProjectById = (id) => filteredProjects.find(p => p.id === parseInt(id));
  const getProjectsByStatus = (status) => filteredProjects.filter(p => p.status === status);

  const pendingProjectsForUser = useMemo(() => {
    // Logic for Approvals
    if (!user) return [];
    if (user.role === userRoles.Funder) {
      // Actually Funder looks at Funding Requests, not just Projects.
      // But if there's a legacy "Approve Project" flow:
      return filteredProjects.filter(p => p.status === 'Pending Approval');
    }
    if (user.role === userRoles.ADMIN) {
      return filteredProjects.filter(p => p.status === 'Pending Approval');
    }
    return [];
  }, [user, filteredProjects]);

  const value = {
    projects: filteredProjects,
    loading,
    error,
    createProject,
    updateProject,
    deleteProject,
    approveProject,
    rejectProject,
    holdProject,
    getProjectById,
    getProjectsByStatus,
    getProjectStats,
    pendingProjectsForUser,
    getSubprojects,
    getProjectRollup,
    promoteMilestoneLead,
    refresh: fetchProjects,
  };

  return (
    <ProjectsContext.Provider value={value}>
      {children}
    </ProjectsContext.Provider>
  )
}

export function useProjects() {
  const context = useContext(ProjectsContext)
  if (!context) {
    throw new Error('useProjects must be used within a ProjectsProvider')
  }
  return context
}
